import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { useParams } from 'react-router-dom';
import { QRCodeSVG } from 'qrcode.react';
import html2pdf from 'html2pdf.js';

const Invoice = () => {
  const { id } = useParams();
  const [invoice, setInvoice] = useState(null);

  useEffect(() => {
    axios.get(`/api/orders/invoice/${id}`)
      .then(res => setInvoice(res.data))
      .catch(err => console.error(err));
  }, [id]);

  if (!invoice) return <p>Loading Invoice...</p>;

  const guaranteeItem = invoice.items.find(item => item.name === 'Guarantee');
  const hasGuarantee = guaranteeItem && (guaranteeItem.price === 'Free' || parseFloat(guaranteeItem.price) > 0);
  const itemsWithoutGuarantee = invoice.items.filter(item => item.name !== 'Guarantee');
  const hasCustomNames = itemsWithoutGuarantee.some(item => item.customName && item.customName.trim() !== '');

  return (
    <div id="invoice-pdf" className="print-landscape bg-white mx-auto shadow px-8 py-4" style={{ width: '210mm', height: '148mm' }}>
      <style>{`
        @media print {
          .no-print { display: none; }
          @page { size: A5 landscape; margin: 0; }
          body { margin: 0; padding: 0; }
        }
      `}</style>


      <div className="flex justify-between items-center mb-2">
        <img src="/assets/logo2.png" alt="Logo" className="h-16" />
        <div className="text-right text-xs">
          <h1 className="text-[25px] font-bold pb-1">MORY GOLD</h1>
          <p className="text-[8px]">City Center, Erbil, Iraq | +964 751 414 1213</p>
        </div>
      </div>

      <h2 className="text-xl font-bold mb-3 text-center">CUSTOM ORDER INVOICE</h2>

      <div className="grid grid-cols-2 gap-4 text-sm mb-3">
        <div>
          <p><strong>Order ID:</strong> {invoice.orderId}</p>
          <p><strong>Customer:</strong> {invoice.customerName}</p>
          <p><strong>Phone:</strong> {invoice.phoneNumber}</p>
          <p><strong>Address:</strong> {invoice.address}</p>
        </div>
        <div className="text-right">
          <p><strong>Ordered Date:</strong> {new Date(invoice.orderDate).toLocaleDateString()}</p>
          <p><strong>Completion Date:</strong> {new Date(invoice.completionDate).toLocaleDateString()}</p>
          {invoice.qrCode && (
            <img
              src={invoice.qrCode}
              alt="Guarantee QR Code"
              className="ml-auto pt-2 w-20 h-20"
            />
          )}

        </div>
      </div>

      <table className="w-full border border-gray-300 text-sm text-center mb-3">
        <thead className="bg-gray-200">
          <tr>
            <th className="border border-gray-300 px-2 py-1">Product ID</th>
            <th className="border border-gray-300 px-2 py-1">Item</th>
            {hasCustomNames && <th className="border border-gray-300 px-2 py-1">Name</th>}
            <th className="border border-gray-300 px-2 py-1">Type</th>
            <th className="border border-gray-300 px-2 py-1">Karat</th>
            <th className="border border-gray-300 px-2 py-1">Weight</th>
            <th className="border border-gray-300 px-2 py-1">Price (USD)</th>
          </tr>
        </thead>

        <tbody>
          {itemsWithoutGuarantee.map((item, idx) => (
            <React.Fragment key={idx}>
              <tr>
                <td className="border px-2 py-1">{item.productId || '—'}</td>
                <td className="border px-2 py-1">{item.name}</td>
                {hasCustomNames && (
                  <td className="border px-2 py-1">{item.customName || '—'}</td>
                )}

                <td className="border px-2 py-1">{item.productType || '—'}</td>
                <td className="border px-2 py-1">21K</td>
                <td className="border px-2 py-1">
                  {typeof item.weight === 'number' ? `${item.weight.toFixed(2)}g` : item.weight || '—'}
                </td>
                <td className={`border px-2 py-1 font-semibold ${item.priceAdjustmentNote ? 'text-red-600' : ''}`}>
                  {typeof item.price === 'number' ? `$${item.price.toFixed(2)}` : item.price || '—'}
                  {item.priceAdjustmentNote && (
                    <span className="ml-1 text-xs text-red-500">🔄</span>
                  )}
                </td>
              </tr>

              {/* Adjustment note if any */}
              {item.priceAdjustmentNote && (
                <tr>
                  <td colSpan={hasCustomNames ? 7 : 6} className="text-right text-xs italic text-gray-600 pr-2">
                    {item.priceAdjustmentNote}
                  </td>
                </tr>
              )}

              {/* Guarantee line */}
              {item.guarantee && item.guarantee !== 'none' && (
                <tr className="bg-green-50 text-green-800 font-medium text-xs leading-tight">
                  <td className="border px-1 py-0.5 text-xs" colSpan={hasCustomNames ? 6 : 5}>
                    This Product Has 1 Year Guarantee, scan the QR code for more information.
                  </td>
                  <td className="border px-1 py-0.5 text-center text-xs align-middle">
                    {item.guarantee === 'free' ? 'Free' : '$5.00'}
                  </td>
                </tr>


              )}
            </React.Fragment>
          ))}

        </tbody>


      </table>

      <div className="text-right text-lg font-semibold text-black">
        <p>
          Total: {typeof invoice.totalPrice === 'number' ? `$${invoice.totalPrice.toFixed(2)}` : '—'}
        </p>
      </div>

      {invoice.priceAdjustmentDescription && (
        <div className="mt-4 text-xs italic whitespace-pre-line">
          {invoice.priceAdjustmentDescription}
        </div>
      )}

      <button
        onClick={() => window.print()}
        className="no-print bg-blue-600 text-white px-4 py-1 mt-4 rounded hover:bg-blue-700"
      >
        Print Invoice
      </button>
      <button
        className="no-print bg-yellow-600 text-white px-4 py-1 mt-4 ml-2 rounded hover:bg-yellow-700"
        onClick={() => {
          const original = document.getElementById('invoice-pdf');

          if (!original) return;

          // Clone the element
          const clone = original.cloneNode(true);

          // Remove all elements with the 'no-print' class
          clone.querySelectorAll('.no-print').forEach((el) => el.remove());

          // Create a temporary container to render cleanly
          const container = document.createElement('div');
          container.style.padding = '0';
          container.style.margin = '0';
          container.appendChild(clone);
          document.body.appendChild(container);

          html2pdf()
            .set({
              margin: 0,
              filename: `${invoice.orderId}.pdf`,
              image: { type: 'jpeg', quality: 0.98 },
              html2canvas: { scale: 2, useCORS: true },
              jsPDF: { unit: 'mm', format: 'a5', orientation: 'landscape' },
            })
            .from(clone)
            .save()
            .then(() => {
              document.body.removeChild(container); // Clean up
            });
        }}
      >
        Export PDF
      </button>

    </div>
  );
};

export default Invoice;
